import { band, Col, DropdownMenu, IconButton, type MenuItem } from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { act } from "../../net/rally.ts";
import { type MenuAct, menuFor } from "../panel/head-menu.ts";
import { snoozeOffers, whenItMeans } from "../snooze.ts";

type OnIt = { private: boolean; away: { snoozedUntil: string | null } } | null;

export function Handles({
  conversation,
  head,
  roomy,
  mayAdd,
  doing,
  tell,
  onDetails,
  detailsShowing,
  onRename,
  onAdd,
  onPickATime,
  onCall,
}: {
  conversation: string;
  head:
    | (OnIt & {
        away: {
          archivedForMe: boolean;
          archivedForAll: boolean;
          pinnedForMe: boolean;
          pinnedForAll: boolean;
          mutedForMe: boolean;
        };
      })
    | null;

  roomy: boolean;

  mayAdd: boolean;

  doing: (what: () => Promise<unknown>) => Promise<void>;
  tell: (text: string, wrong?: boolean) => void;
  onDetails: () => void;
  detailsShowing: boolean;
  onRename: () => void;
  onAdd: () => void;
  onPickATime: () => void;

  onCall?: ((withCamera: boolean) => void) | undefined;
}) {
  const target = { target: { kind: "conversation", id: conversation } };
  const away = head?.away;

  const now = new Date();

  const snoozeUntil = async (until: Date) => doing(() => act("attention.snooze", { ...target, until }));

  const copyALink = async () => {
    const link = `${window.location.origin}/c/${conversation}`;
    try {
      await navigator.clipboard.writeText(link);
      tell(say("head.copied"));
    } catch {
      tell(say("head.notCopied"), true);
    }
  };

  const snoozeRows: MenuItem[] = [
    ...snoozeOffers(now).map((offer) => ({
      label: offer.label,
      hint: whenItMeans(offer.until, now),
      onPress: () => void snoozeUntil(offer.until),
    })),
    { label: say("snooze.pickATime"), onPress: () => onPickATime() },
  ];

  const presses: Record<MenuAct, () => void> = {
    pin: () => void doing(() => act("attention.pin", target)),
    unpin: () =>
      void doing(() =>
        act("attention.unpin", {
          ...target,
          ...(away?.pinnedForAll && !away.pinnedForMe ? { scope: "all" } : {}),
        }),
      ),

    snooze: () => onPickATime(),
    wake: () => void doing(() => act("attention.unsnooze", target)),
    archive: () => void doing(() => act("attention.archive", target)),
    unarchive: () =>
      void doing(() =>
        act("attention.unarchive", {
          ...target,
          ...(away?.archivedForAll && !away.archivedForMe ? { scope: "all" } : {}),
        }),
      ),
    add: onAdd,
    rename: onRename,
    copyLink: () => void copyALink(),

    makePrivate: () => void doing(() => act("conversation.setPrivate", { conversation, private: true })),
    makeReadable: () => void doing(() => act("conversation.setPrivate", { conversation, private: false })),
  };

  const items: MenuItem[] = menuFor({
    pinned: Boolean(away?.pinnedForMe || away?.pinnedForAll),
    muted: Boolean(away?.mutedForMe),
    snoozed: Boolean(away?.snoozedUntil),
    archived: Boolean(away?.archivedForMe || away?.archivedForAll),
    private: Boolean(head?.private),
    mayAdd,
  }).map((row) => ({
    label: row.label,
    icon: row.icon,
    hint: row.hint,
    separator: row.separator,
    ...(row.submenu ? { submenu: snoozeRows } : { onPress: presses[row.act] }),
  }));

  const callRows: MenuItem[] = onCall
    ? [
        { label: say("call.video"), icon: "video", onPress: () => onCall(true) },
        { label: say("call.audio"), icon: "mic", onPress: () => onCall(false) },
      ]
    : [];

  return (
    <Col testID="conversation-actions" style={{ flexDirection: "row", alignItems: "center", gap: band.gap }}>
      {roomy ? null : (
        <IconButton
          icon={detailsShowing ? "panel-right-close" : "panel-right-open"}
          ariaLabel={detailsShowing ? say("panel.hide") : say("panel.open")}
          testID="conversation-details"
          onPress={onDetails}
        />
      )}
      {onCall ? (
        <DropdownMenu
          items={callRows}
          width={200}
          align="right"
          trigger={
            <Col testID="conversation-call" style={{ pointerEvents: "none" }}>
              <IconButton icon="video" ariaLabel={say("call.choose")} />
            </Col>
          }
        />
      ) : null}
      <DropdownMenu
        items={items}
        back={say("snooze.back")}
        width={260}
        align="right"
        trigger={
          <Col testID="conversation-more" style={{ pointerEvents: "none" }}>
            <IconButton icon="more-horizontal" ariaLabel={say("head.more")} />
          </Col>
        }
      />
    </Col>
  );
}
